/**
 * Vignobles › À découvrir (/vignobles/decouvrir) — carnet des domaines à visiter puis découverts.
 * Ajout intelligent (lien Maps / site / texte / photo), menus contextuels sur l'adresse et le
 * lien web (ouvrir dans Maps, copier…), passage en « découvert », création de la fiche domaine.
 * Aucune suggestion IA, aucune carte. Prend en charge le partage natif (share_target).
 */
import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, Globe, MapPin, MoreHorizontal, Search, Check, RotateCcw, Building2, Trash2, Copy, Navigation, Grape } from 'lucide-react';
import { vignoblesApi } from '../../services/api';
import { Button, Spinner, EmptyState, SegmentedControl, PageTitle } from '../../components/ui/kit';
import { Drawer } from '../../components/ui/Drawer';
import { InlineEditable } from '../../components/wine/InlineEditable';
import { VoiceDictateButton } from '../../components/wine/VoiceDictateButton';
import { RECO_SOURCE_LABELS } from '../../constants';
import { formatDate } from '../../utils/format';
import { useUndoToast } from '../../hooks/useUndoToast';
import QuickAddDrawer from './QuickAddDrawer';

const TABS = [{ value: 'a_decouvrir', label: 'À découvrir' }, { value: 'decouvert', label: 'Découverts' }];
const SORTS = [{ value: 'date', label: 'Date' }, { value: 'nom', label: 'Nom' }, { value: 'ville', label: 'Ville' }];

const mapsUrl = (r) => {
  const q = (r.lat != null && r.lng != null) ? `${r.lat},${r.lng}`
    : [r.address, r.postal_code, r.city, r.country].filter(Boolean).join(', ');
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
};
const hostOf = (url) => { try { return new URL(url).hostname.replace(/^www\./, ''); } catch { return url; } };
const copy = (text, label) => { navigator.clipboard?.writeText(text).then(() => toast.success(`${label} copié`)).catch(() => toast.error('Copie impossible')); };

export default function Decouvrir() {
  const qc = useQueryClient();
  const { notify } = useUndoToast();
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState('a_decouvrir');
  const [q, setQ] = useState('');
  const [sort, setSort] = useState('date');
  const [adding, setAdding] = useState(false);
  const [sharedInput, setSharedInput] = useState('');
  const [menuId, setMenuId] = useState(null);
  const [discoverFor, setDiscoverFor] = useState(null);
  const [selected, setSelected] = useState(null);

  // Partage natif (share_target) : ouvre l'ajout pré-rempli.
  useEffect(() => {
    const shared = params.get('text') || params.get('url') || params.get('title');
    if (shared) { setSharedInput(shared); setAdding(true); setParams({}, { replace: true }); }
  }, [params, setParams]);

  const { data: recos = [], isLoading } = useQuery({
    queryKey: ['recos', tab, q, sort],
    queryFn: () => vignoblesApi.recommendations({ status: tab, q, sort }),
  });
  const counts = useQuery({ queryKey: ['recos-counts'], queryFn: () => vignoblesApi.recommendations({}) });
  const refresh = () => { qc.invalidateQueries({ queryKey: ['recos'] }); qc.invalidateQueries({ queryKey: ['recos-counts'] }); };
  const countFor = (s) => (counts.data || []).filter((r) => r.status === s).length;

  const undiscover = async (id) => { await vignoblesApi.undiscovered(id); refresh(); };
  const createDomain = async (r) => { await vignoblesApi.recoToDomain(r.id); toast.success('Fiche domaine créée'); qc.invalidateQueries({ queryKey: ['domains'] }); refresh(); };
  const remove = async (r) => {
    await vignoblesApi.deleteReco(r.id); refresh();
    notify('Recommandation supprimée', async () => { await vignoblesApi.createReco(r); refresh(); });
  };

  const tabsWithCounts = TABS.map((t) => ({ ...t, label: `${t.label} (${countFor(t.value)})` }));

  return (
    <div className="p-4 md:p-6 max-w-4xl" onClick={() => setMenuId(null)}>
      <div className="flex items-start justify-between gap-3 mb-4">
        <PageTitle kicker="Vignobles" title="À découvrir" />
        <Button variant="primary" onClick={() => { setSharedInput(''); setAdding(true); }} data-testid="add-reco-btn"><Plus className="w-4 h-4" /> À découvrir</Button>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-5">
        <SegmentedControl options={tabsWithCounts} value={tab} onChange={setTab} testId="decouvrir-tabs" />
        <div className="relative flex-1 min-w-[180px]">
          <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-n500" />
          <input className="vd-input h-9 pl-8" placeholder="Rechercher (nom, ville, notes…)" value={q} onChange={(e) => setQ(e.target.value)} data-testid="reco-search" />
        </div>
        <SegmentedControl options={SORTS} value={sort} onChange={setSort} testId="reco-sort" />
      </div>

      {isLoading ? <Spinner /> : recos.length === 0 ? (
        <EmptyState title={tab === 'a_decouvrir' ? 'Aucun domaine à découvrir' : 'Aucun domaine découvert'}
          description="Ajoutez un domaine depuis un lien Google Maps, un site web, une photo ou un nom." testId="reco-empty" />
      ) : (
        <ol className="space-y-2" data-testid="reco-list">
          {recos.map((r) => {
            const lastNote = r.notes?.[r.notes.length - 1];
            const address = [r.address, r.postal_code, r.city].filter(Boolean).join(', ');
            return (
              <li key={r.id} className="vd-card p-3 relative" data-testid={`reco-${r.id}`}>
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-gold700 mt-0.5 shrink-0" strokeWidth={1.6} />
                  <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setSelected(r)} data-testid={`reco-open-${r.id}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-title text-xl" style={{ fontFamily: 'Cormorant, serif' }}>{r.name}</span>
                      <span className="text-[11px] uppercase tracking-wide text-n500">{RECO_SOURCE_LABELS[r.source_type] || r.source_type}</span>
                    </div>
                    {r.website && (
                      <a href={r.website.startsWith('http') ? r.website : `https://${r.website}`} target="_blank" rel="noreferrer"
                        onClick={(e) => e.stopPropagation()} className="text-[13px] text-accent-700 hover:underline inline-flex items-center gap-1"
                        onContextMenu={(e) => { e.preventDefault(); setMenuId(`web-${r.id}`); }} data-testid={`reco-web-${r.id}`}>
                        <Globe className="w-3.5 h-3.5" /> {hostOf(r.website)}
                      </a>
                    )}
                    {address && (
                      <button className="block text-[13px] text-n700 text-left hover:text-ink"
                        onClick={(e) => { e.stopPropagation(); setMenuId(`addr-${r.id}`); }}
                        onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); setMenuId(`addr-${r.id}`); }}
                        data-testid={`reco-address-${r.id}`}>
                        {address}
                      </button>
                    )}
                    {lastNote && <p className="text-[13px] text-n600 italic mt-1 line-clamp-2">« {lastNote.text} »</p>}
                    <p className="text-[12px] text-n500 mt-1">
                      {r.status === 'decouvert' && r.visited_at ? `Découvert le ${formatDate(r.visited_at)}` : `Ajouté le ${formatDate(r.created_at)}`}
                    </p>
                  </div>
                  <button className="p-1.5 text-n500 hover:text-ink shrink-0" onClick={(e) => { e.stopPropagation(); setMenuId(menuId === r.id ? null : r.id); }} data-testid={`reco-menu-${r.id}`}>
                    <MoreHorizontal className="w-5 h-5" />
                  </button>
                </div>

                {/* Menu adresse */}
                {menuId === `addr-${r.id}` && (
                  <Menu>
                    <MenuItem icon={Navigation} onClick={() => { window.open(mapsUrl(r), '_blank'); setMenuId(null); }} testId={`reco-openmaps-${r.id}`}>Ouvrir dans Google Maps</MenuItem>
                    <MenuItem icon={Copy} onClick={() => { copy(address, 'Adresse'); setMenuId(null); }} testId={`reco-copyaddr-${r.id}`}>Copier l'adresse</MenuItem>
                  </Menu>
                )}
                {/* Menu site web */}
                {menuId === `web-${r.id}` && (
                  <Menu>
                    <MenuItem icon={Globe} onClick={() => { window.open(r.website.startsWith('http') ? r.website : `https://${r.website}`, '_blank'); setMenuId(null); }}>Ouvrir le site</MenuItem>
                    <MenuItem icon={Copy} onClick={() => { copy(r.website, 'Lien'); setMenuId(null); }}>Copier le lien</MenuItem>
                  </Menu>
                )}
                {/* Menu actions ⋯ */}
                {menuId === r.id && (
                  <Menu>
                    {address && <MenuItem icon={Navigation} onClick={() => { window.open(mapsUrl(r), '_blank'); setMenuId(null); }} testId={`reco-openmaps2-${r.id}`}>Ouvrir dans Google Maps</MenuItem>}
                    {address && <MenuItem icon={Copy} onClick={() => { copy(address, 'Adresse'); setMenuId(null); }}>Copier l'adresse</MenuItem>}
                    {r.status === 'a_decouvrir'
                      ? <MenuItem icon={Check} onClick={() => { setDiscoverFor(r); setMenuId(null); }} testId={`reco-discover-${r.id}`}>Passer en découvert</MenuItem>
                      : <MenuItem icon={RotateCcw} onClick={() => { undiscover(r.id); setMenuId(null); }} testId={`reco-undiscover-${r.id}`}>Repasser à découvrir</MenuItem>}
                    <MenuItem icon={Building2} onClick={() => { createDomain(r); setMenuId(null); }} testId={`reco-createdomain-${r.id}`}>Créer la fiche domaine</MenuItem>
                    <MenuItem icon={Trash2} danger onClick={() => { remove(r); setMenuId(null); }} testId={`reco-delete-${r.id}`}>Supprimer</MenuItem>
                  </Menu>
                )}
              </li>
            );
          })}
        </ol>
      )}

      <QuickAddDrawer open={adding} onClose={() => setAdding(false)} onSaved={() => { setAdding(false); refresh(); }} initialInput={sharedInput} />
      {selected && <RecoFiche reco={selected} open={!!selected} onClose={() => setSelected(null)} onChanged={refresh}
        onDiscover={() => { setDiscoverFor(selected); setSelected(null); }}
        onUndiscover={() => { undiscover(selected.id); setSelected(null); }}
        onCreateDomain={() => { createDomain(selected); setSelected(null); }}
        onDelete={() => { remove(selected); setSelected(null); }} />}
      {discoverFor && <DiscoverModal reco={discoverFor} onClose={() => setDiscoverFor(null)} onDone={() => { setDiscoverFor(null); refresh(); }} />}
    </div>
  );
}

function Menu({ children }) {
  return <div className="absolute right-3 top-12 z-20 bg-paper border border-hair rounded-md shadow-lg py-1 min-w-[200px] animate-fade-in" onClick={(e) => e.stopPropagation()} data-testid="reco-context-menu">{children}</div>;
}
function MenuItem({ icon: Icon, children, onClick, danger, testId }) {
  return (
    <button onClick={onClick} data-testid={testId} className={`w-full flex items-center gap-2 px-3 h-9 text-[13px] text-left hover:bg-surface ${danger ? 'text-red-700' : 'text-ink'}`}>
      <Icon className="w-4 h-4" strokeWidth={1.6} /> {children}
    </button>
  );
}

/** Volet flottant d'une recommandation : édition en direct des champs, notes horodatées, actions. */
function RecoFiche({ reco, open, onClose, onChanged, onDiscover, onUndiscover, onCreateDomain, onDelete }) {
  const [r, setR] = useState(reco);
  const [note, setNote] = useState('');
  const navigate = useNavigate();
  const { data: tastings = [] } = useQuery({
    queryKey: ['reco-tastings', r.id],
    queryFn: () => vignoblesApi.recoTastings(r.id),
    enabled: !!r.id && open,
  });
  const patch = async (field, value) => { const up = await vignoblesApi.updateReco(r.id, { [field]: value }); setR(up); onChanged(); };
  const addNote = async () => { if (!note.trim()) return; const up = await vignoblesApi.updateReco(r.id, { add_note: note.trim() }); setR(up); setNote(''); onChanged(); };
  const FIELDS = [['name', 'Nom'], ['website', 'Site web'], ['address', 'Adresse'], ['postal_code', 'Code postal'], ['city', 'Ville'], ['phone', 'Téléphone'], ['email', 'E-mail']];
  return (
    <Drawer open={open} onClose={onClose} title={r.name} testId="reco-fiche"
      footer={(
        <div className="flex flex-wrap justify-end gap-2">
          {r.status === 'a_decouvrir'
            ? <Button variant="primary" onClick={onDiscover} data-testid="reco-fiche-discover"><Check className="w-4 h-4" /> Découvert</Button>
            : <Button onClick={onUndiscover} data-testid="reco-fiche-undiscover"><RotateCcw className="w-4 h-4" /> À découvrir</Button>}
          <Button onClick={onCreateDomain} data-testid="reco-fiche-createdomain"><Building2 className="w-4 h-4" /> Créer le domaine</Button>
          <Button onClick={onDelete} className="text-red-700" data-testid="reco-fiche-delete"><Trash2 className="w-4 h-4" /> Supprimer</Button>
        </div>
      )}>
      <div className="grid grid-cols-2 gap-x-4 gap-y-2 mb-5">
        {FIELDS.map(([k, label]) => (
          <div key={k} className={k === 'name' || k === 'address' || k === 'website' ? 'col-span-2' : ''}>
            <span className="text-[11px] uppercase tracking-wide text-n500">{label}</span>
            <InlineEditable value={r[k]} onSave={(v) => patch(k, v)} testId={`reco-fiche-${k}`} />
          </div>
        ))}
      </div>
      {tastings.length > 0 && (
        <div className="mb-5" data-testid="reco-tastings">
          <p className="kicker mb-2">Dégustation(s) liée(s)</p>
          <ul className="space-y-1.5">
            {tastings.map((t) => (
              <li key={t.id}>
                <button onClick={() => navigate(`/cave/degustes?open=${t.id}`)}
                  className="w-full flex items-center gap-2 text-left text-[13.5px] text-accent-700 hover:underline" data-testid={`reco-tasting-${t.id}`}>
                  <Grape className="w-3.5 h-3.5 shrink-0" />
                  <span className="flex-1">{t.name}{t.tasting_date ? ` · ${formatDate(t.tasting_date)}` : ''}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div>
        <p className="kicker mb-2">Notes</p>
        <ul className="space-y-1.5 mb-2">
          {(r.notes || []).map((n, i) => (
            <li key={i} className="text-[13px] text-n700" data-testid={`reco-fiche-note-${i}`}>
              « {n.text} » <span className="text-[11px] text-n500">— {n.author} · {formatDate(n.date)}</span>
            </li>
          ))}
          {!(r.notes || []).length && <li className="text-[13px] text-n500">Aucune note.</li>}
        </ul>
        <div className="flex gap-2 items-center">
          <input className="vd-input h-9 flex-1" placeholder="Ajouter une note…" value={note} onChange={(e) => setNote(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') addNote(); }} data-testid="reco-fiche-newnote" />
          <VoiceDictateButton onTranscribe={(t) => setNote((p) => (p ? `${p} ${t}` : t))} showLabel={false} testId="reco-fiche-note-mic" />
          <Button onClick={addNote} data-testid="reco-fiche-addnote">Ajouter</Button>
        </div>
      </div>
    </Drawer>
  );
}

function DiscoverModal({ reco, onClose, onDone }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await vignoblesApi.discovered(reco.id, date, note); toast.success('Marqué découvert'); onDone(); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 animate-fade-in" onClick={onClose}>
      <div className="bg-paper border border-hair rounded-lg shadow-xl p-5 w-full max-w-sm" onClick={(e) => e.stopPropagation()} data-testid="discover-modal">
        <h3 className="font-title text-2xl mb-3">Passer en découvert</h3>
        <p className="text-[13px] text-n600 mb-3">{reco.name}</p>
        <label className="block mb-3"><span className="text-[12px] text-n600">Date de la visite</span>
          <input type="date" className="vd-input h-9" value={date} onChange={(e) => setDate(e.target.value)} data-testid="discover-date" /></label>
        <label className="block mb-4"><span className="text-[12px] text-n600">Note</span>
          <textarea className="vd-input py-2 h-auto" rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="discover-note" /></label>
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Annuler</Button>
          <Button variant="primary" onClick={save} disabled={busy} data-testid="discover-save">Confirmer</Button>
        </div>
      </div>
    </div>
  );
}
